import { ViewNav } from '@printos/agnostic-view-nav';

/**
 * @ngdoc directive
 * @name gazelle.components.appNav.directive:gzAppNav
 *
 * @param {*} items Pass in either a callback method that will return an array of menu items, or pass in an array of menu items directly.
 *
 * @description
 * `<gz-app-nav>` is a frame component that is responsible for rendering the application navigation menu items (currently) on the left side of the page.
 * It accepts one parameter (items) that can be a callback to a function in the application that returns the menu item structure or the menu array itself.
 * The difference between the two is that when passing in the array the application would already have to have created the menu items and have everything
 * resolved before invoking gz-app-nav.  When passing in the callback, Gazelle will make the call to the function to retrieve the menu items. The callback
 * should return a promise.  When the promise is resolved the result will be displayed.This can result in a later binding of the navigation menu creation.
 *
 * The structure of the JSON menu items array is as follows:
 * ```
 * [
 *  {
 *   id: '',
 *   loc: {
 *     name: 'Home'
 *   },
 *   icon: {
 *     type: 'fa',
 *     name: 'home'
 *   },
 *   state: 'home'
 *   },
 *   {
 *     id: '',
 *     loc: {
 *       name: 'Item2'
 *     },
 *     icon: {
 *       type: 'fa',
 *       name: 'newspaper-o'
 *     },
 *     state: 'item2',
 *     views: [{
 *         id: '',
 *         loc: {
 *           name: 'Sub-nav item-1'
 *         },
 *         icon: {
 *           type: 'fa',
 *           name: 'nav1'
 *         },
 *         state: "subnav-1"
 *     }]
 *   }
 *  }
 * ]
 * ```
 *
 * id: is a unique id for the menu item within the entire array
 * loc: Localized strings - items here must be localized before sending to the directive (the directive will not localize these values).
 *   name: The localized name of the menu item for display to the user either as label text, hover, or some other affordance.
 * icon: The icon definition
 *   type: The type of icon, 'fa' = FontAwesome, 'md' = Material Design, 'svg' = SVG image.
 *   name: The name of the icon in the icon set (fa, md).
 *    For SVGs this is the URL to retrieve the image, which can be previously stored in the $templateCache.
 *    See `src/mini-apps/app-switcher/cache-icons.run.js` for an example.
 * state: The ui-router state to transition to when this menu item is selected.
 *  States can also be ui-router future states as long as they define a `lazyLoad` function which returns a promise that resolves
 *  if the state exists and is rejected if the state does not exist.
 * views: A nested version of this structure to support sub-menu items.

 * @example
 * Jade
 * ```jade
 * gz-app-nav(items='function()')
 * -or-
 * gz-app-nav(items='object')
 * ```
 *
 * HTML
 * ```html
 * <gz-app-nav items='function()'></gz-app-nav>
 * -or-
 * <gz-app-nav items='object'></gz-app-nav>
 * ```
 */
export const name = 'gzAppNav';
export default function gzAppNav(
  $log,
  $q,
  $rootScope,
  $state,
  $futureState,
  $gzGo,
  $injector,
  $window
) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let viewNav;

  let directive = {
    restrict: 'EA',
    scope: {
      items: '&',
      theme: '@',
    },
    link: gzAppNavLink,
    controller: gzAppNavController,
    controllerAs: 'vm',
    bindToController: true
  };

  return directive;

  //////////////////////
  // Private functions

  function gzAppNavLink(scope, element, attrs) {
    const props = {
      theme: scope.vm.theme,
    };
    viewNav = new ViewNav({
      target: element[0],
      props
    });
  }

  //* @ngInject
  function gzAppNavController($scope, $translate, $templateCache, $state, rnApplication, rnClient) {
    let navMenuItems = [];

    let vm = this;

    vm.$onInit = onInit;
    return;

    function onInit() {
      let promises = [];
      promises.push(loadMenuItems());
      promises.push($translate.onReady());

      return $q.all(promises).then(() => {
        viewNav.$set({ selectedId: $state.current.name });
        $rootScope.$on('$stateChangeSuccess', (event, toState) => {
          viewNav.$set({ selectedId: toState.name });
        });

        let views = navMenuItems.map(item => {
          let view = mapViewNavItem(item);
          if (item.children) {
            view.children = item.children.map(mapViewNavItem);
          }

          return view;
        });

        let config = {
          views,
        };

        let appId = rnApplication.getCurrentAppId();
        if (appId) {
          config.pairingId = appId;
        }
        viewNav.$set(config);
        $scope.$watch($gzGo.showAppNav, showViewNav => {
          viewNav.$set({hideNav: !showViewNav});
        });
      });
    }

    function mapViewNavItem(item) {
      let view = {
        id: item.id,
      };

      if (item.showInNav !== undefined) {
        view.showInNav = item.showInNav;
      }

      view.name = item.name;
      if (!view.name && _.get(item, 'loc.name')) {
        view.name = $translate.instant(item.loc.name);
      }

      if ($state.get(item.id)) {
        let href = $state.href(item.id, {}, {inherit: false});
        if (href) {
          view.href = href;
        }
      }
      else {
        let futureStates = $futureState.get();
        if (futureStates) {
          let futureState = futureStates[item.id];
          if (futureState) {
            view.href = `#${futureState.url}`;
          }
        }
      }

      view.description = item.description;
      if (item.descriptionKey) {
        view.description = $translate.instant(item.descriptionKey);
      }

      // TODO: figure out fa and md types
      let iconType = _.get(item, 'icon.type');
      switch (iconType) {
        case 'svg':
          view.icon = {
            svg: $templateCache.get(item.icon.name)
          };
          break;
        case 'svg-svg':
          view.icon = {
            svg: item.icon.name
          };
          break;
        case 'svg-url':
          view.icon = {
            svgPath: item.icon.name
          };
          break;
      }

      // Check for additional children that do not show in nav from route definition
      let itemStateConfig = $state.get(item.id);
      if (itemStateConfig && itemStateConfig.childStates) {
        if (!view.children) {
          view.children = [];
        }
        itemStateConfig.childStates.forEach(childState => {
          let found = view.children.find(c => {
            return c.id === childState;
          });

          if (!found) {
            view.children.push({id: childState, showInNav: false});
          }
        });
      }

      return view;
    }

    function ensureLazyLoadedStates() {
      let futureStates = $futureState.get();
      let promises = [];
      for (let futureStateName in futureStates) {
        if (!futureStates.hasOwnProperty(futureStateName)) {
          continue;
        }

        let futureState = futureStates[futureStateName];
        let promise = checkFutureState(futureState);
        if (promise) {
          promises.push(promise);
        }
      }
      return $q.all(promises);
    }

    function loadMenuItems() {
      // $log.debug('In gzAppNavController loadMenuItems');
      // Ensure vm.items is defined (at least as a function returning undefined
      vm.items = vm.items || (() => undefined);

      return $q.when(vm.items())
        .then(items => {
          navMenuItems = items || [];

          // Ensure backwards compatibility
          ensureChildren(navMenuItems);
          ensureId(navMenuItems);
          ensureId(navMenuItems.children);
        })
        .then(ensureLazyLoadedStates)
        .then(filterOutInvalidFutureStates)
        .catch(error => $log.error('Unable to retrieve application views', error));
    }

    function ensureId(items) {
      if (items) {
        items.forEach(item => {
          // Map state to id for legacy view items
          item.id = item.state ? item.state : item.id;
          delete item.state;
        });
      }
    }

    function ensureChildren(items) {
      items.forEach(item => {
        if (item.views) {
          // Map views to children for legacy view items
          item.children = item.views;
          delete item.views;
        }
      });
    }

    function checkFutureState(futureState) {
      if (futureState.lazyLoad) {
        return $injector.invoke(futureState.lazyLoad, futureState, {futureState}).catch(() => {
          // no-op - want to ensure all promises resolve here
        });
      }
    }

    function filterOutInvalidFutureStates() {
      navMenuItems.forEach(item => {
        if (item.children) {
          item.children = item.children.filter(isValidFutureState);
        }
      });

      navMenuItems = navMenuItems.filter(item => {
        if (item.children && item.children.length > 0) {
          return true;
        }

        return isValidFutureState(item);
      });
    }

    function isValidFutureState(item) {
      let futureStates = $futureState.get();
      let state = $state.get(item.id);
      if (!state) {
        let futureState = futureStates[item.id];
        if (!futureState || (futureState && !futureState.include)) {
          return false;
        }
      }
      return true;
    }
  }
}
